/**
 * Locked CaseFile Banner Component
 *
 * Displayed when a CaseFile is locked for any reason (ACL v2.3).
 * Lock reasons:
 * - pending_patient_authorization: Shows authorization options (in-app / upload)
 * - pending_adult_transition: Shows reauthorization guidance
 * - authorization_revoked: Shows revocation notice with support CTA
 *
 * Selecting an authorization option renders the corresponding form inline.
 */

import { useState } from 'react';
import {
  Lock,
  PenTool,
  Upload,
  Shield,
  ChevronRight,
  ArrowLeft,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { InAppAuthorizationForm } from './InAppAuthorizationForm';
import { UploadAuthorizationForm } from './UploadAuthorizationForm';
import { getLockMessage } from '../../utils/lockMessages';
import type { CaseFileLockReason } from '../../types/settings';

// ============================================================================
// Types
// ============================================================================

interface LockedCaseFileBannerProps {
  caseFileId: string;
  caseFileName: string;
  lockReason?: CaseFileLockReason | string;
  careRecipientName?: string;
  careRecipientDob?: string;
  careRecipientEmail?: string;
  onUnlocked: () => void;
}

type AuthorizationOption = 'none' | 'in_app' | 'upload';

// ============================================================================
// Main Component
// ============================================================================

export function LockedCaseFileBanner({
  caseFileId,
  caseFileName,
  lockReason,
  careRecipientName,
  careRecipientDob,
  careRecipientEmail,
  onUnlocked,
}: LockedCaseFileBannerProps): JSX.Element {
  const [selectedOption, setSelectedOption] = useState<AuthorizationOption>('none');
  const lockMessage = getLockMessage(lockReason);

  const handleCancel = () => {
    setSelectedOption('none');
  };

  // For pending_patient_authorization: show authorization forms when selected
  if (lockReason === 'pending_patient_authorization') {
    if (selectedOption === 'in_app') {
      return (
        <div className="bg-white border border-brass/30 rounded-xl shadow-sm p-6 mb-6">
          <button
            type="button"
            onClick={handleCancel}
            className="btn-ghost btn-sm flex items-center gap-1 text-muted mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to options
          </button>
          <InAppAuthorizationForm
            caseFileId={caseFileId}
            caseFileName={caseFileName}
            careRecipientName={careRecipientName}
            careRecipientDob={careRecipientDob}
            careRecipientEmail={careRecipientEmail}
            onComplete={onUnlocked}
            onCancel={handleCancel}
          />
        </div>
      );
    }

    if (selectedOption === 'upload') {
      return (
        <div className="bg-white border border-brass/30 rounded-xl shadow-sm p-6 mb-6">
          <button
            type="button"
            onClick={handleCancel}
            className="btn-ghost btn-sm flex items-center gap-1 text-muted mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to options
          </button>
          <UploadAuthorizationForm
            caseFileId={caseFileId}
            caseFileName={caseFileName}
            careRecipientName={careRecipientName}
            careRecipientDob={careRecipientDob}
            careRecipientEmail={careRecipientEmail}
            onComplete={onUnlocked}
            onCancel={handleCancel}
          />
        </div>
      );
    }
  }

  // Determine icon based on lock reason
  const BannerIcon = lockReason === 'authorization_revoked' ? AlertTriangle
    : lockReason === 'pending_adult_transition' ? RefreshCw
    : Lock;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl shadow-sm mb-6 overflow-hidden">
      {/* Banner Header */}
      <div className="p-6 pb-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
            <BannerIcon className="w-6 h-6 text-amber-700" />
          </div>
          <div>
            <h2 className="text-lg font-display text-amber-900 mb-1">
              {lockMessage.title}
            </h2>
            <p className="text-sm text-amber-700">
              {lockMessage.description}
            </p>
          </div>
        </div>
      </div>

      {/* Action area — varies by lock reason */}
      {lockReason === 'pending_patient_authorization' && (
        <div className="px-6 pb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Option A: In-App E-Signature */}
            <button
              type="button"
              onClick={() => setSelectedOption('in_app')}
              className="bg-white border border-soft-neutral rounded-xl p-5 text-left hover:border-evergreen hover:shadow-md transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-evergreen/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-evergreen/20 transition-colors">
                  <PenTool className="w-5 h-5 text-evergreen" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-ink">Sign In-App</h3>
                    <ChevronRight className="w-4 h-4 text-muted group-hover:text-evergreen transition-colors" />
                  </div>
                  <p className="text-sm text-muted mb-3">
                    Patient is present and can sign on this device
                  </p>
                  <div className="flex items-center gap-2">
                    <Shield className="w-3.5 h-3.5 text-evergreen" />
                    <span className="text-xs text-evergreen font-medium">
                      HIPAA-Compliant E-Signature
                    </span>
                  </div>
                </div>
              </div>
            </button>

            {/* Option B: Upload Signed Document */}
            <button
              type="button"
              onClick={() => setSelectedOption('upload')}
              className="bg-white border border-soft-neutral rounded-xl p-5 text-left hover:border-evergreen hover:shadow-md transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-brass/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-brass/20 transition-colors">
                  <Upload className="w-5 h-5 text-brass" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-ink">Upload Signed Form</h3>
                    <ChevronRight className="w-4 h-4 text-muted group-hover:text-evergreen transition-colors" />
                  </div>
                  <p className="text-sm text-muted mb-3">
                    Upload a scan or photo of the signed authorization
                  </p>
                  <div className="flex items-center gap-2">
                    <Shield className="w-3.5 h-3.5 text-brass" />
                    <span className="text-xs text-brass-text-warm font-medium">
                      Download Blank Form Available
                    </span>
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>
      )}

      {lockReason === 'pending_adult_transition' && (
        <div className="px-6 pb-6">
          <p className="text-sm text-amber-800">
            To continue managing this CaseFile, the care recipient (now an adult) must
            provide their own authorization. Start the reauthorization process to
            transition the CaseFile to their adult consent.
          </p>
        </div>
      )}

      {lockReason === 'authorization_revoked' && (
        <div className="px-6 pb-6">
          <p className="text-sm text-amber-800">
            You can no longer make changes to this CaseFile. If you believe this was
            done in error, contact the care recipient or their representative to
            request re-authorization.
          </p>
        </div>
      )}
    </div>
  );
}

export default LockedCaseFileBanner;
